import { useMemo, useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import {
    describeEmployeePerformanceError,
    employeeColumnValue,
    employeeInitials,
    formatEmployeeValue,
    nextEmployeeSort,
    resolveEmployeeSettings,
    sortEmployees,
    visibleEmployeeColumns,
    type EmployeeColumnDef,
    type EmployeeColumnKey,
    type EmployeePerformanceData,
    type EmployeePerformanceRow,
    type EmployeePerformanceSettings,
    type SortDir,
} from '../../lib/employeePerformance'

// ── Employee Performance widget: the table ──────────────────────────────
//
// Sales, discounts and Adventure Bucks per employee for whatever the dashboard is showing. Column
// order, visibility, the saved sort and the options come from the widget's settings (edited in
// EmployeePerformanceConfigModal); clicking a header re-sorts for this viewing only — the saved
// sort comes back whenever the settings change it, and nothing a header click does is persisted.

export interface EmployeePerformancePanelProps {
    data: EmployeePerformanceData | undefined
    settings: EmployeePerformanceSettings | undefined
    /** e.g. 'Today', 'Sep 12', 'September 2026', 'Aug 1 – Aug 31, 2026'. */
    periodLabel: string
    /** 'all' | 'instore' | 'online'. */
    channel: string
    /** NET (true) / GROSS (false) — renames the sales column the way the Net Sales card is renamed. */
    includeReturns: boolean
    /** 'group' | 'all' | a store GUID. */
    selectedStoreId: string
    /** True while there is no data yet. */
    isLoading: boolean
    /** The react-query error (an EmployeePerformanceError when the server answered), or null. */
    error: unknown
}

const ONLINE_BUCKS_NOTE = 'Online orders don’t earn Adventure Bucks at the till.'

const channelSuffix = (channel: string) =>
    channel === 'instore' ? 'In-Store' : channel === 'online' ? 'Online' : null

const ariaSortFor = (dir: SortDir) => (dir === 'asc' ? 'ascending' : 'descending')

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
    const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown
    return <Icon size={12} aria-hidden="true" className="emp-perf-sort-icon" />
}

export default function EmployeePerformancePanel({
    data, settings, periodLabel, channel, includeReturns, selectedStoreId, isLoading, error,
}: EmployeePerformancePanelProps) {
    const resolved = useMemo(() => resolveEmployeeSettings(settings), [settings])
    const columns = useMemo(() => visibleEmployeeColumns(resolved.columns), [resolved.columns])
    const ctx = { includeReturns }

    // A header click sorts for this viewing only. The saved sort is the baseline, and whenever the
    // settings change it (the column editor's Apply, a layout load) the local choice is dropped so the
    // table shows what was just configured. Adjusting state during render, not in an effect, means the
    // stale order is never painted.
    const basis = `${resolved.sortKey}:${resolved.sortDir}`
    const [picked, setPicked] = useState<{ key: EmployeeColumnKey; dir: SortDir }>({ key: resolved.sortKey, dir: resolved.sortDir })
    const [sortBasis, setSortBasis] = useState(basis)
    if (sortBasis !== basis) {
        setSortBasis(basis)
        setPicked({ key: resolved.sortKey, dir: resolved.sortDir })
    }
    // A header sort also lasts only while its column is on screen. Hide that column in the editor and
    // the saved sort takes over, rather than the rows (and their rank numbers) staying ordered by a
    // figure nobody can see, with no header marked. The pick is dropped, not just masked, so showing
    // the column again doesn't re-order the table without a click. It can't loop: when the saved
    // sort's own column is hidden, picked already equals the saved sort.
    const pickedShown = columns.some(c => c.key === picked.key)
    if (!pickedShown && (picked.key !== resolved.sortKey || picked.dir !== resolved.sortDir)) {
        setPicked({ key: resolved.sortKey, dir: resolved.sortDir })
    }
    const sort = pickedShown ? picked : { key: resolved.sortKey, dir: resolved.sortDir }

    const sorted = useMemo(
        () => (data ? sortEmployees(data.employees, sort.key, sort.dir, data.totals) : []),
        [data, sort.key, sort.dir],
    )
    // Bars are scaled to the top seller across EVERY employee in the view, so a row keeps its bar
    // length when the row limit or the sort changes.
    const maxSales = useMemo(
        () => (data ? data.employees.reduce((m, r) => (r.netSales > m ? r.netSales : m), 0) : 0),
        [data],
    )

    const limited = resolved.rowLimit > 0 && sorted.length > resolved.rowLimit
    const shown = limited ? sorted.slice(0, resolved.rowLimit) : sorted
    const multiStore = selectedStoreId === 'group' || selectedStoreId === 'all'
    const bucksVisible = columns.some(c => c.key === 'bucks')
    const onlineNote = channel === 'online' && bucksVisible
    const suffix = channelSuffix(channel)
    const count = data?.employees.length

    const storeLine = (row: EmployeePerformanceRow) => {
        if (!row.homeStoreName) return null
        if (multiStore) return row.homeStoreName
        return (row.homeStoreId ?? '').toLowerCase() !== selectedStoreId.toLowerCase() ? row.homeStoreName : null
    }

    const headerTitle = (def: EmployeeColumnDef) =>
        def.key === 'bucks' && channel === 'online' ? `${def.description} ${ONLINE_BUCKS_NOTE}` : def.description

    const numClass = (def: EmployeeColumnDef) =>
        def.numeric ? `emp-perf-num${def.key === 'sales' ? ' emp-perf-sales' : ''}` : undefined

    let body: ReactNode
    if (error) {
        body = <div className="emp-perf-state" role="status">{describeEmployeePerformanceError(error)}</div>
    } else if (!data) {
        body = <div className="emp-perf-state" role="status">{isLoading ? 'Loading employee figures…' : 'Waiting for employee figures…'}</div>
    } else if (data.employees.length === 0) {
        body = <div className="emp-perf-state" role="status">No sales recorded for this period</div>
    } else {
        body = (
            <div className="chart-panel-content">
                <table className="emp-perf-table">
                    <thead>
                        <tr>
                            {columns.map(def => {
                                const active = def.sortable && sort.key === def.key
                                return (
                                    <th
                                        key={def.key}
                                        scope="col"
                                        data-col={def.key}
                                        className={def.key === 'rank' ? 'emp-perf-rank' : numClass(def)}
                                        aria-sort={active ? ariaSortFor(sort.dir) : undefined}
                                        title={headerTitle(def)}
                                    >
                                        {def.sortable ? (
                                            <button type="button" className="emp-perf-sort" onClick={() => setPicked(nextEmployeeSort(sort, def.key))}>
                                                <span>{def.label(ctx)}</span>
                                                <SortIcon active={active} dir={sort.dir} />
                                            </button>
                                        ) : def.key === 'rank' ? (
                                            <><span aria-hidden="true">#</span><span className="sr-only">Rank</span></>
                                        ) : def.label(ctx)}
                                    </th>
                                )
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((row, i) => (
                            <tr key={row.employeeId} className={row.departed ? 'is-departed' : undefined}>
                                {columns.map(def => {
                                    if (def.key === 'rank') return <td key="rank" className="emp-perf-rank">{i + 1}</td>
                                    if (def.key === 'employee') {
                                        const store = storeLine(row)
                                        const name = row.name.trim()
                                        // The row header: a screen reader moving down a figure column
                                        // announces whose figure it is.
                                        return (
                                            <th key="employee" scope="row" className="emp-perf-employee">
                                                <div className="emp-perf-person">
                                                    <span className="emp-perf-avatar" aria-hidden="true">{employeeInitials(name)}</span>
                                                    <span className="emp-perf-ident">
                                                        <span className="emp-perf-line">
                                                            <span className={`emp-perf-name${name ? '' : ' is-unknown'}`} title={name || undefined}>{name || 'Unknown employee'}</span>
                                                            {row.departed && <span className="emp-perf-tag" title="No longer with the company — these sales are still theirs">former</span>}
                                                        </span>
                                                        {store && <span className="emp-perf-sub">{store}</span>}
                                                    </span>
                                                </div>
                                            </th>
                                        )
                                    }
                                    const value = employeeColumnValue(def.key, row, data.totals)
                                    const text = formatEmployeeValue(def.format, value)
                                    if (def.key === 'sales') {
                                        const share = maxSales > 0 ? Math.min(1, Math.max(0, row.netSales / maxSales)) : 0
                                        return (
                                            <td key="sales" className={numClass(def)}>
                                                <span className="emp-perf-value">{text}</span>
                                                {resolved.showBars && (
                                                    <span className="emp-perf-bar" aria-hidden="true">
                                                        <span className="emp-perf-bar-fill" style={{ width: `${(share * 100).toFixed(1)}%` }} />
                                                    </span>
                                                )}
                                            </td>
                                        )
                                    }
                                    return <td key={def.key} className={numClass(def)}>{text}</td>
                                })}
                            </tr>
                        ))}
                    </tbody>
                    {resolved.showTotals && (
                        <tfoot>
                            <tr>
                                {columns.map(def => {
                                    if (def.key === 'rank') return <td key="rank" className="emp-perf-rank" />
                                    if (def.key === 'employee') {
                                        return (
                                            <th key="employee" scope="row" className="emp-perf-employee">
                                                <span className="emp-perf-total-label">Total</span>
                                                {limited && <span className="emp-perf-sub">All {sorted.length} employees</span>}
                                            </th>
                                        )
                                    }
                                    const value = employeeColumnValue(def.key, data.totals, data.totals)
                                    return <td key={def.key} className={numClass(def)}>{formatEmployeeValue(def.format, value)}</td>
                                })}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        )
    }

    const showFoot = !error && !!data && data.employees.length > 0 && (limited || onlineNote)

    return (
        <div className="chart-panel emp-perf" style={{ height: '100%' }}>
            <div className="chart-panel-title emp-perf-title">
                <span className="emp-perf-heading">
                    Employee Performance
                    <span className="emp-perf-meta">
                        {periodLabel && <> · {periodLabel}</>}
                        {suffix && <> · {suffix}</>}
                    </span>
                </span>
                {count !== undefined && !error && (
                    <span className="emp-perf-count">{count} {count === 1 ? 'employee' : 'employees'}</span>
                )}
            </div>
            {body}
            {showFoot && (
                <div className="emp-perf-foot">
                    {limited && <span>Showing top {shown.length} of {sorted.length}</span>}
                    {onlineNote && <span>{ONLINE_BUCKS_NOTE}</span>}
                </div>
            )}
        </div>
    )
}
